import { clsx } from 'clsx'
import { type FC } from 'react'

import {
  type PimProductV12,
  type PimProductV13,
  type PimProductWithVariantsV1,
} from '@redteclab/api/clients/bully'
import { type GkAirRecommendedProductDataWeb } from '@redteclab/api/clients/crosssellers-proxy-service'

import { useGlobalConfigContext } from '../../../global-config'
import { type LOCALE } from '../../../i18n'
import { SELLER_ID_SAE } from '../../../order'
import { ProductPicture } from '../../../product'
import {
  recommendationAccessorGetLocalizedProductImageAltText,
  recommendationAccessorGetLocalizedProductImageUrl,
} from '../../../recommendation'
import { cartIntermediateAccessorGetProductName } from '../../model/cartIntermediateAccessorGetProductName'
import { type CartIntermediateEntryObject } from '../../model/CartIntermediateContextValue'

import { CartIntermediateProductInfoQuantity } from './CartIntermediateProductInfoQuantity'
import { CartIntermediateProductInfoRedpoints } from './CartIntermediateProductInfoRedpoints'
import { CartIntermediateProductInfoSellerLink } from './CartIntermediateProductInfoSellerLink/CartIntermediateProductInfoSellerLink'

const getProductImageProps = (
  product:
    | GkAirRecommendedProductDataWeb
    | PimProductV12
    | PimProductV13
    | PimProductWithVariantsV1,
  locale: LOCALE,
): { alt: string; src?: string } => {
  if ('product_images' in product) {
    const image = product.product_images?.at(0)

    return {
      alt: image?.alt_text ?? '',
      src: image?.public_url,
    }
  }

  if ('images' in product) {
    return {
      alt: '',
      src: product.images?.at(0),
    }
  }

  if ('UID' in product) {
    return {
      alt: recommendationAccessorGetLocalizedProductImageAltText({
        locale,
        product,
      }),
      src: recommendationAccessorGetLocalizedProductImageUrl({
        locale,
        product,
      }),
    }
  }

  return {
    alt: '',
  }
}

export const CartIntermediateProductInfo: FC<{
  entry: CartIntermediateEntryObject | null
}> = ({ entry }) => {
  const config = useGlobalConfigContext()
  const { locale } = config

  if (!entry?.product) {
    return null
  }

  const { product, redpoints, sellerData } = entry

  const productName = cartIntermediateAccessorGetProductName({
    locale,
    product,
  })

  const shouldDisplayPartnerproductsLink = Boolean(
    sellerData && sellerData.sellerId !== SELLER_ID_SAE,
  )

  return (
    <div
      className={clsx(
        'flex h-full flex-col rounded-m bg-light-primary-low p-4 tablet:basis-1/2',
        shouldDisplayPartnerproductsLink && 'pb-2',
      )}
    >
      <div className="flex h-full items-center gap-3">
        <ProductPicture
          className="shrink-0"
          imageProps={{
            ...getProductImageProps(product, locale),
            height: 80,
            width: 80,
          }}
        />
        <div className="flex flex-col gap-3">
          <div className="line-clamp-3 overflow-hidden text-ellipsis font-medium text-dark-primary-max no-underline">
            {productName}
          </div>
          <CartIntermediateProductInfoQuantity />
          <CartIntermediateProductInfoRedpoints redPoints={redpoints} />
        </div>
      </div>
      {shouldDisplayPartnerproductsLink && sellerData ? (
        <div className="text-start">
          <CartIntermediateProductInfoSellerLink
            config={config}
            sellerData={sellerData}
          />
        </div>
      ) : null}
    </div>
  )
}
